import type { FC, SetStateAction } from "react";
import { useForm } from "@tanstack/react-form";

import { usePatchPatient } from "@/api/patients";
import Edit from "@/assets/icons/edit";
import { FieldInput } from "@/components/form";
import DialogCustom from "@/components/modals/dialog-custom";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { patchIndexesSchema } from "@/schemas/patient";
import type { Patient } from "@/types/api/patient";
import { resetForm } from "@/utils/form";

interface PatchPatientIndexesModalProps {
  patient: Patient;
  isOpen: boolean;
  setIsOpen: React.Dispatch<SetStateAction<boolean>>;
}

const PatchPatientIndexesModal: FC<PatchPatientIndexesModalProps> = ({
  patient,
  isOpen,
  setIsOpen,
}) => {
  const { mutate } = usePatchPatient(patient.id);

  const { bleedingIndex, plaqueIndex } = patient;

  const {
    Field,
    handleSubmit,
    state: { errors },
  } = useForm({
    defaultValues: {
      bleedingIndex: bleedingIndex ?? null,
      plaqueIndex: plaqueIndex ?? null,
    },
    onSubmit: async ({ value, formApi }) => {
      const values = patchIndexesSchema.parse(value);
      mutate(value, {
        onSuccess: () => {
          setIsOpen(false);
          resetForm(values, formApi);
        },
      });
    },
    validators: {
      onSubmit: ({
        value: {
          plaqueIndex: plaqueIndexValue,
          bleedingIndex: bleedingIndexValue,
        },
      }) => {
        return {
          fields: {
            bleedingIndex: bleedingIndexValue
              ? bleedingIndexValue < 0 || bleedingIndexValue > 100
                ? "L'indice de saignement doit être entre 0 et 100"
                : undefined
              : undefined,
            plaqueIndex: plaqueIndexValue
              ? plaqueIndexValue < 0 || plaqueIndexValue > 100
                ? "L'indice de plaque doit être entre 0 et 100"
                : undefined
              : undefined,
          },
        };
      },
    },
  });

  return (
    <DialogCustom
      title="Modifier les informations"
      startIcon={
        <div className="flex p-3 w-12 h-12 justify-center items-center border border-gray-200 rounded-[10px]">
          <Edit className="w-6 h-6" color="black" />
        </div>
      }
      open={isOpen}
      onOpenChange={() => setIsOpen(false)}
      className="flex flex-col gap-4"
    >
      <Separator />
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full px-6">
        <FieldInput
          name="plaqueIndex"
          label="Indice de plaque (IP)"
          placeholder="0"
          Field={Field}
          min={0}
          max={100}
          type="number"
          errors={errors[0] ? true : false}
        />
        <FieldInput
          name="bleedingIndex"
          label="Indice de saignement (IS)"
          placeholder="0"
          min={0}
          max={100}
          Field={Field}
          type="number"
          errors={errors[1] ? true : false}
        />
        <input type="submit" className="hidden" />
      </form>
      <Separator />
      <DialogFooter className="flex gap-3 px-6">
        <Button
          type="button"
          className="w-full"
          onClick={() => {
            setIsOpen(false);
          }}
          variant={"outline"}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          className="w-full"
          variant={"blue"}
          onClick={() => {
            handleSubmit();
          }}
        >
          Générer
        </Button>
      </DialogFooter>
    </DialogCustom>
  );
};

export default PatchPatientIndexesModal;
